import React, { useState, useEffect } from 'react';
import { get, post, put, del } from '../utils/api';
import { useAuth } from '../context/AuthContext';
const fmt = n => '\u20a6' + Number(n||0).toLocaleString('en-NG');

export default function Products() {
  const { user, hasPermission } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = () => {
    setLoading(true);
    get('/products').then(d => setProducts(d.data||[])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (p) => {
    if (!window.confirm('Deactivate '+p.name+'? It will be hidden from the system.')) return;
    try { await del('/products/'+p.id); load(); }
    catch (err) { alert(err.message); }
  };


  const updateUnitCost = async product => {
    const supplied =
      window.prompt(
        'Enter the approved unit production cost for ' +
        product.name,
        String(product.unit_cost || 0)
      );

    if (supplied === null) return;

    const unitCost = Number(supplied);

    if (
      !Number.isFinite(unitCost) ||
      unitCost < 0
    ) {
      alert(
        'Enter a valid cost of zero or greater.'
      );
      return;
    }

    const reason =
      window.prompt(
        'State the reason or cost basis. ' +
        'For example: approved standard production cost.'
      );

    if (
      reason === null ||
      reason.trim().length < 5
    ) {
      alert(
        'A clear reason is required.'
      );
      return;
    }

    try {
      await put(
        '/products/' + product.id + '/cost',
        {
          unit_cost: unitCost,
          reason: reason.trim()
        }
      );

      await load();
    } catch (error) {
      alert(
        error.message ||
        'The unit cost could not be updated.'
      );
    }
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <h2 className="page-title">Finished Goods</h2>
        {hasPermission('products:write') && (
          <button className="btn btn-primary" onClick={() => setModal({})}>+ Add Product</button>
        )}
      </div>
      <div className="tbl-wrap">
        {loading ? <div className="spinner"></div> : (
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Size</th><th>Color</th>
                <th style={{textAlign:'right'}}>Unit Cost</th>
                <th style={{textAlign:'right'}}>Inventory Value</th>
                <th style={{textAlign:'right'}}>Selling Price</th>
                <th style={{textAlign:'right'}}>Stock</th>
                <th>Reorder Level</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0
                ? <tr><td colSpan="10"><div className="empty-state"><h3>No products yet</h3></div></td></tr>
                : products.map(p => (
                  <tr key={p.id}>
                    <td style={{fontWeight:500}}>{p.name}</td>
                    <td>{p.size||'—'}</td>
                    <td>{p.color||'—'}</td>
                    <td className="amt">{fmt(p.unit_cost)}</td>
                    <td className="amt">{fmt(Number(p.current_stock||0) * Number(p.unit_cost||0))}</td>
                    <td className="amt">{fmt(p.selling_price)}</td>
                    <td className="amt" style={{fontWeight:600,color:p.current_stock<=p.reorder_level?'var(--unpaid)':'inherit'}}>
                      {Number(p.current_stock||0).toLocaleString()}
                    </td>
                    <td style={{textAlign:'center'}}>{p.reorder_level||0}</td>
                    <td>
                      <span className={'badge '+(p.current_stock<=p.reorder_level?'b-part':'b-paid')}>
                        {p.current_stock<=p.reorder_level?'Low Stock':'OK'}
                      </span>
                    </td>
                    <td>
                      <div className="flex-center gap-sm">
                        {user?.role === 'super_admin' && (
                          <button className="btn btn-outline btn-sm" onClick={() => updateUnitCost(p)}>Set Cost</button>
                        )}
                        {hasPermission('products:write') && (
                          <button className="btn btn-ghost btn-sm" onClick={() => setModal(p)}>Edit</button>
                        )}
                        {hasPermission('products:delete') && (
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p)}>Delete</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        )}
      </div>
      {modal !== null && (
        <ProductModal product={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
    </div>
  );
}

function ProductModal({ product, onClose, onSaved }) {
  const isEdit = !!product?.id;
  const [form, setForm] = useState({
    name: product?.name||'',
    size: product?.size||'',
    color: product?.color||'',
    description: product?.description||'',
    selling_price: product?.selling_price||'',
    current_stock: product?.current_stock||0,
    reorder_level: product?.reorder_level||0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!form.name) { setError('Name required'); return; }
    setSaving(true);
    setError('');
    try {
      if (isEdit) await put('/products/'+product.id, form);
      else await post('/products', form);
      onSaved();
    } catch (err) { 
      console.error('Error:', err);
      setError(err.message); 
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{maxWidth:500}}>
        <div className="modal-hdr">
          <span className="modal-title">{isEdit?'Edit':'Add'} Product</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>X</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error mb-md">{error}</div>}
          <div className="form-group">
            <label className="form-label">Product Name *</label>
            <input className="form-control" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Size</label>
              <input className="form-control" placeholder="e.g. Small, Medium, 30x40cm"
                value={form.size} onChange={e=>setForm({...form,size:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Color</label>
              <input className="form-control" placeholder="e.g. Natural, Black"
                value={form.color} onChange={e=>setForm({...form,color:e.target.value})} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Selling Price (N)</label>
              <input type="number" className="form-control" value={form.selling_price}
                onChange={e=>setForm({...form,selling_price:e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">Current Stock</label>
              <input type="number" className="form-control" value={form.current_stock}
                onChange={e=>setForm({...form,current_stock:e.target.value})} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Reorder Level</label>
            <input type="number" className="form-control" value={form.reorder_level}
              onChange={e=>setForm({...form,reorder_level:e.target.value})} />
            <div className="form-hint">Alert when stock falls below this number</div>
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-control" rows={2} value={form.description||''}
              onChange={e=>setForm({...form,description:e.target.value})} />
          </div>
        </div>
        <div className="modal-ftr">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving?'Saving...':isEdit?'Save Changes':'Add Product'}
          </button>
        </div>
      </div>
    </div>
  );
}
